import { FormEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TailSpin } from "react-loader-spinner";
import Button from "../../components/flat/ui/Button";
import AuthPasswordLayout, {
  AUTH_ILLUSTRATIONS,
  AuthLoginFooter,
  AuthPasswordSuccessView,
  ButtonText,
  Container,
  Div,
  ErrorMessage,
  Form,
  FormField,
  PanelIllustration,
  RequiredFieldLabel,
  StyledInput,
  Subtitle,
  Title,
} from "../Auth/components/AuthPasswordLayout";
import { trackPasswordResetEmailSent } from "../Auth/trackPasswordEvents";
import type { RootStateWithResetPassword } from "../PasswordReset/resetPassword.types";

export default function ForgotPassword() {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [localError, setLocalError] = useState("");
  const loadingRequestReset = useSelector(
    (state: RootStateWithResetPassword) =>
      state.resetPassword.loading_request_reset
  );
  const requestResetData = useSelector(
    (state: RootStateWithResetPassword) =>
      state.resetPassword.request_reset_data
  );

  const isSuccess =
    Object.keys(requestResetData).length > 0 && Boolean(requestResetData.success);
  const isError =
    Object.keys(requestResetData).length > 0 && !requestResetData.success;

  useEffect(() => {
    if (isSuccess) {
      trackPasswordResetEmailSent();
    }
  }, [isSuccess]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalError("");

    if (!email.trim()) {
      setLocalError("Informe seu e-mail para continuar.");
      return;
    }

    dispatch({
      type: "REQUEST_RESET_PASSWORD",
      payload: {
        data: { email: email.trim() },
        callback: () => {
          setEmail("");
        },
      },
    });
  };

  return (
    <AuthPasswordLayout
      rightContent={
        <PanelIllustration src={AUTH_ILLUSTRATIONS.INSERT_PASSWORD} alt="" />
      }
    >
      <Container>
        {isSuccess ? (
          <AuthPasswordSuccessView
            title="E-mail enviado com sucesso"
            subtitle="Enviamos um link de recuperação para o e-mail informado. Verifique sua caixa de entrada (ou o spam)."
            buttonLabel="Voltar para o login"
            buttonTo="/auth/login"
          />
        ) : (
          <>
            <Div>
              <Title>Redefinição de senha</Title>
              <Subtitle>
                Informe o e-mail cadastrado para receber as instruções
              </Subtitle>
            </Div>

            {isError && (
              <ErrorMessage>
                {typeof requestResetData.msg === "string"
                  ? requestResetData.msg
                  : "Ocorreu um erro ao solicitar a redefinição. Tente novamente."}
              </ErrorMessage>
            )}

            <Form onSubmit={handleSubmit}>
              <FormField>
                <RequiredFieldLabel htmlFor="email">E-mail</RequiredFieldLabel>
                <StyledInput
                  id="email"
                  type="email"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  disabled={loadingRequestReset}
                  autoComplete="email"
                />
              </FormField>

              {localError && <ErrorMessage>{localError}</ErrorMessage>}

              <Button
                type="submit"
                width="100%"
                padding="10px 40px"
                borderRadius="5px"
                hoverBackgroundColor="#007656"
                disabled={loadingRequestReset}
              >
                <ButtonText>
                  {loadingRequestReset ? (
                    <TailSpin
                      visible
                      height="20"
                      width="20"
                      color="#ffffff"
                      ariaLabel="loading"
                    />
                  ) : (
                    "Enviar instruções"
                  )}
                </ButtonText>
              </Button>
            </Form>

            <AuthLoginFooter />
          </>
        )}
      </Container>
    </AuthPasswordLayout>
  );
}
